import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { AccordionModule } from 'primeng/accordion';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DialogModule } from 'primeng/dialog';
import { FieldsetModule } from 'primeng/fieldset';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeTableModule } from 'primeng/treetable';

import { firstValueFrom, forkJoin, Subscription } from 'rxjs';

import { SETTINGS, Settings } from '../../../app.settings';

import { afterRender } from '../../../common/after-render';
import { notifyAndTrackError } from '../../../common/error';
import { isEmpty, isNotEmpty, isNotStringEmpty, isStringEmpty } from '../../../common/functions';
import { successMsg, warningMsg } from '../../../common/messages';
import { catalogInsectSort, catalogSubstrateSort, catalogWorkflowNotitificationSort } from '../../../common/sorts';

import { CanComponentDeactivate, ProcessingComponent, UnsavedChangesDialogComponent } from '../../../components';

import { AppInsightsService } from '../../../services/app-insights.service';
import { CatalogAdminService, ICatalog, ICatalogUpdateRequest, ILocation, LocationAdminService } from '../../../services/insecta';
import { MSGraphService } from '../../../services/msgraph.service';

import { ModsService } from '../../../services/insecta/mods.service';
import { TabContainerComponent } from './tabContainers/tab-containers.component';
import { TabDeliveryDaysComponent } from './tabDeliveryDays/tab-delivery-days.component';
import { TabInsectComponent } from './tabInsects/tab-insects.component';
import { TabNotificationComponent } from './tabNotifications/tab-notifications.component';
import { TabOrderItemTypeComponent } from './tabOrderItemType/tab-orderitemtype.component';
import { TabPrintingComponent } from './tabPrinters/tab-printing.component';
import { TabProjectsComponent } from './tabProjects/tab-projects.component';
import { TabSubstrateComponent } from './tabSubstrates/tab-substrates.component';
import { TabUndeliverableDatesComponent } from './tabUndeliverableDates/tab-undeliverable-dates.component';

@Component({
  selector: 'app-site-catalog-admin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    AccordionModule,
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ContextMenuModule,
    CommonModule,
    DialogModule,
    FloatLabelModule,
    FieldsetModule,
    InputNumberModule,
    InputTextModule,
    PanelModule,
    SelectModule,
    TableModule,
    TabsModule,
    TextareaModule,
    ToggleSwitchModule,
    TreeSelectModule,
    TreeTableModule,
    ProcessingComponent,
    TabContainerComponent,
    TabDeliveryDaysComponent,
    TabInsectComponent,
    TabProjectsComponent,
    TabNotificationComponent,
    TabSubstrateComponent,
    TabUndeliverableDatesComponent,
    UnsavedChangesDialogComponent,
    TabOrderItemTypeComponent,
    TabPrintingComponent
  ],

  templateUrl: 'site-catalog-admin.component.html'
})
export class SiteCatalogAdminComponent implements OnDestroy, OnInit, CanComponentDeactivate {
  @ViewChild('tabContainer') tabContainer!: TabContainerComponent;
  @ViewChild('tabPrinting') tabPrinting!: TabPrintingComponent;
  printingTabLoaded: boolean = false;
  printingDataLoaded: boolean = false;
  tabPanels = {
    tabInsects: '0',
    tabSubstrates: '1',
    tabContainer: '2',
    tabSettings: '3'
  };
  tabValue: any = this.tabPanels.tabInsects;

  emptyGuid: string = `00000000-0000-0000-0000-000000000000`;

  dirtyConfirm$: ((leave: boolean) => void) | null = null;

  catalog: ICatalog = <ICatalog>{
    id: this.emptyGuid
  };
  location: ILocation = <ILocation>{
    id: this.emptyGuid
  };

  pageVars: any = {
    breadcrumbItems: <MenuItem[]>[{ label: 'Site Catalogs', routerLink: '/admin/catalogs' }, { label: 'Site Catalog & Settings' }],
    currentUser: ``,
    isDirty: false,
    isEditing: false,
    isReady: false,
    pageId: `Admin:SiteCatalog`,
    showDirtyDlg: false,
    tabsReady: <boolean[]>[true, true, true, true, true, true]
  };

  catalog$!: Subscription;
  catalogSettings: any = {
    leadTime: null,
    leadTimeMessage: null,
    siteCode: null,
    isEditing: false
  };

  recipesLookup: any[] = [];
  isRecipesLookupForbidden: boolean = false;

  constructor(
    private catalogService: CatalogAdminService,
    private locationService: LocationAdminService,
    private appInsights: AppInsightsService,
    private graphService: MSGraphService,
    private msgService: MessageService,
    private activatedRoute: ActivatedRoute,
    private modsService: ModsService,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:SiteCatalog`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/sitecatalog`);
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) return Promise.resolve(true);

    return new Promise((resolve) => {
      this.pageVars.showDirtyDlg = true;
      const confirm$ = (decision: boolean) => {
        this.dirtyConfirm$ = null;
        resolve(decision);
      };
      this.dirtyConfirm$ = confirm$;
    });
  }

  dirtyConfirm(leave: boolean) {
    this.pageVars.showDirtyDlg = false;
    if (this.dirtyConfirm$) {
      this.dirtyConfirm$(leave);
    }
  }

  ngOnDestroy() {
    this.catalog$.unsubscribe();
    this.location = <ILocation>{
      id: this.emptyGuid
    };
    this.pageVars = {
      isReady: false
    };
  }

  onPrintingAccordionClick() {
    if (!this.printingTabLoaded) {
      this.printingTabLoaded = true;
    }
    if (this.printingTabLoaded && !this.printingDataLoaded) {
      afterRender(() => {
        if (this.tabPrinting) {
          this.tabPrinting.loadBartenderData();
          this.printingDataLoaded = true;
        }
      });
    }
  }

  ngOnInit() {
    this.pageVars.tabsReady = [false, false, false, false, false];
    this.pageVars.isReady = false;

    this.catalog$ = this.activatedRoute.paramMap.subscribe((params) => {
      const request = {
        catalog: this.catalogService.getCatalogByLocation(<string>params.get('locationId')),
        location: this.locationService.getLocation(<string>params.get('locationId')),
        profile: this.graphService.getMyProfile$()
      };

      firstValueFrom(forkJoin(request))
        .then((result) => {
          this.catalog = <ICatalog>result.catalog;

          this.catalog.insects = this.catalog.insects.sort(catalogInsectSort);
          this.catalog.substrates = this.catalog.substrates.sort(catalogSubstrateSort);
          this.catalog.workflowNotifications = this.catalog.workflowNotifications.sort(catalogWorkflowNotitificationSort);

          this.catalogSettings.leadTime = this.catalog.leadTime;
          this.catalogSettings.leadTimeMessage = this.catalog.leadTimeMessage;
          this.location = <ILocation>result.location;

          this.pageVars.currentUser = result.profile.userPrincipalName;
          this.pageVars.breadcrumbItems = <MenuItem[]>[
            { label: 'Site Catalogs', routerLink: '/admin/catalogs' },
            { label: `${this.location.name} Site Catalog` }
          ];
          this.pageVars.isReady = true;
          this.getRecipesLookup();
        })
        .catch((err) => {
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: this.pageVars.pageId, action: 'Init:Error' });
        });
    });
  }

  onSiteCodeChange() {
    if (!this.catalog.siteCode || this.catalog.siteCode.length !== 2) {
      warningMsg(this.msgService, 'Site Code must be exactly 2 characters');
      return;
    }

    this.catalogSettings.isEditing = true;
    this.catalogSettings.siteCode = this.catalog.siteCode;

    const request = <ICatalogUpdateRequest>{
      id: this.catalog.id,
      locationId: this.catalog.locationId,
      siteCode: this.catalog.siteCode,
      actionBy: this.pageVars.currentUser
    };

    firstValueFrom(this.catalogService.updateCatalog(request))
      .then(() => {
        this.catalog.siteCode = request.siteCode;
        this.catalog.action = 'UPD';
        this.catalog.actionBy = this.pageVars.currentUser;
        this.catalog.actionOn = new Date(Date.now());
        successMsg(this.msgService, 'Site Code updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: this.pageVars.pageId, action: 'SiteCode:Update:Error' });
      });
  }

  showProcessingDlg() {
    if (this.pageVars.tabsReady.filter((x: boolean) => x === false).length > 0) return true;
    return !this.pageVars.isReady;
  }

  tglTabsReady(idx: number, event: any) {
    this.pageVars.tabsReady[idx] = event;
    // If all tabs are ready, set isReady to true
    if (this.pageVars.tabsReady.every((x: boolean) => x === true)) {
      this.pageVars.isReady = true;
    }
  }

  catalogSettingsCancel() {
    this.catalogSettings.leadTime = this.catalog.leadTime;
    this.catalogSettings.leadTimeMessage = this.catalog.leadTimeMessage;
    this.catalogSettings.isEditing = false;
  }

  catalogSettingsSave() {
    this.pageVars.isReady = false;

    const request = <ICatalogUpdateRequest>{
      id: this.catalog.id,
      locationId: this.catalog.locationId,
      siteCode: this.catalog.siteCode,
      leadTime: this.catalogSettings.leadTime,
      leadTimeMessage: this.catalogSettings.leadTimeMessage,
      actionBy: this.pageVars.currentUser
    };

    firstValueFrom(this.catalogService.updateCatalog(request))
      .then(() => {
        this.catalog.leadTime = this.catalogSettings.leadTime;
        this.catalog.leadTimeMessage = this.catalogSettings.leadTimeMessage;
        this.catalog.siteCode = this.catalogSettings.siteCode;
        this.catalog.action = 'UPD';
        this.catalog.actionBy = this.pageVars.currentUser;
        this.catalog.actionOn = new Date(Date.now());
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: this.pageVars.pageId, action: 'CatalogSettings:Save:Error' });
        this.catalogSettings.catalog = this.catalog;
      })
      .finally(() => {
        this.catalogSettings.isEditing = false;
        this.pageVars.isReady = true;
      });
  }

  catalogSettingsValid() {
    if (isEmpty(this.catalogSettings.leadTime) && isStringEmpty(this.catalogSettings.leadTimeMessage)) return true;
    if (isEmpty(this.catalogSettings.leadTime) && isNotStringEmpty(this.catalogSettings.leadTimeMessage)) return false;
    if (isNotEmpty(this.catalogSettings.leadTime) && isStringEmpty(this.catalogSettings.leadTimeMessage)) return false;
    return true;
  }

  tabPanelChange() {
    if (this.tabValue !== this.tabPanels.tabContainer) {
      this.tabContainer.resetComponent();
    }
  }

  getRecipesLookup() {
    this.pageVars.isReady = false;
    firstValueFrom(this.modsService.getRecipesLookup())
      .then((response) => {
        this.recipesLookup = Object.entries(response).map(([id, name]) => ({ recipeId: id, recipeName: name }));
        this.pageVars.isReady = true;
      })
      .catch((err) => {
        if (err.status === 403) {
          this.isRecipesLookupForbidden = true;
        }
        this.recipesLookup = [];
        this.pageVars.isReady = true;
      });
  }
}
